/**
 * Auth Callback Page
 *
 * Handles the redirect from Supabase after email verification.
 * Supabase redirects to /auth/callback with tokens in the URL hash.
 *
 * This component:
 * 1. Waits for Supabase to process the callback
 * 2. Shows loading state while processing
 * 3. Shows confirmation message on success
 * 4. Shows error if something goes wrong
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

// Capture the hash immediately so we can process tokens before React Router clears it.
const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
const SET_SESSION_TIMEOUT_MS = 5000;

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  }
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<{ email?: string } | null>(null);
  const hasHandledCallback = useRef(false);
  const capturedHashRef = useRef(initialHash);

  useEffect(() => {
    let cancelled = false;

    // Handle the OAuth callback
    const handleCallback = async () => {
      // Check if already handled (for Strict Mode)
      if (hasHandledCallback.current) {
        return;
      }
      hasHandledCallback.current = true;
      const hashToProcess = capturedHashRef.current;

      const clearSensitiveHash = () => {
        if (typeof window !== 'undefined') {
          window.history.replaceState(
            {},
            document.title,
            `${window.location.pathname}${window.location.search}`
          );
        }
      };

      try {
        // Parse hash to check for errors first (before processing)
        const hashParams = new URLSearchParams(hashToProcess.substring(1));
        const error = hashParams.get('error');
        const errorCode = hashParams.get('error_code');
        const errorDescription = hashParams.get('error_description');

        // Handle Supabase errors in URL
        if (error) {
          clearSensitiveHash();
          if (errorCode === 'otp_expired') {
            // This could mean the link is old OR it was already used
            // Check if user already exists and is confirmed
            setError('This verification link has expired or was already used. If you already confirmed your email, please try logging in. Otherwise, request a new verification email by signing up again.');
            return;
          }

          setError(`Verification failed: ${errorDescription || error}. Please try logging in or request a new verification email.`);
          return;
        }

        // If no hash, check if user is already logged in
        if (!hashToProcess || hashToProcess === '#') {
          const { data: { session } } = await supabase.auth.getSession();

          if (session) {
            setSuccessInfo({
              email: session.user.email || undefined,
            });
            return;
          }

          navigate('/login', { replace: true });
          return;
        }

        // Manually extract tokens from hash and set session
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (!accessToken || !refreshToken) {
          clearSensitiveHash();
          setError('Invalid verification link. Missing authentication tokens.');
          return;
        }

        const setSessionPromise = supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        let sessionResponse;
        try {
          sessionResponse = await withTimeout(setSessionPromise, SET_SESSION_TIMEOUT_MS, 'supabase.auth.setSession');
        } catch {
          clearSensitiveHash();
          setError('We confirmed your email, but the automatic login timed out. Please return to the app and log in manually.');
          return;
        }

        if (cancelled) {
          return;
        }

        const { data: sessionData, error: setSessionError } = sessionResponse;

        if (setSessionError || !sessionData?.session) {
          clearSensitiveHash();
          setError('Failed to complete verification. Please try logging in manually.');
          return;
        }

        const session = sessionData.session;
        clearSensitiveHash();
        // Wait a moment for profile creation trigger to complete
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Check if profile exists, create if needed
        const { error: profileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();

        if (profileError) {
          // If profile doesn't exist, try to create it manually
          if (profileError.code === 'PGRST116') {
            const { error: createError } = await supabase
              .from('user_profiles')
              .insert({
                id: session.user.id,
                email: session.user.email || '',
                full_name: session.user.user_metadata?.full_name || null,
              });

            if (createError) {
              setError('Profile creation failed. Please contact support.');
              return;
            }
          }
        }

        setSuccessInfo({
          email: session.user.email || undefined,
        });
        return;

      } catch (err) {
        clearSensitiveHash();
        setError(`An unexpected error occurred: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    };

    let callbackCompleted = false;

    handleCallback().then(() => {
      callbackCompleted = true;
    }).catch(err => {
      if (!cancelled) {
        setError(`An unexpected error occurred: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    });

    // Cleanup function to detect if effect is cancelled
    return () => {
      cancelled = true;
      // If callback didn't complete and we're cleaning up, reset the flag
      // so the next mount can try again (important for Strict Mode)
      if (!callbackCompleted) {
        hasHandledCallback.current = false;
      }
    };
  }, [navigate]);

  if (successInfo) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          padding: '3rem',
          maxWidth: '500px',
          backdropFilter: 'blur(10px)'
        }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Email confirmed</h1>
          <p style={{ marginBottom: '1rem', opacity: 0.9 }}>
            {successInfo.email ? `Thanks, ${successInfo.email} is verified.` : 'Thanks, your email is verified.'}
          </p>
          <p style={{ marginBottom: '2rem', opacity: 0.8 }}>
            You can return to your original tab and log in now to continue.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            style={{
              padding: '1rem 2rem',
              background: 'white',
              color: '#667eea',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          padding: '3rem',
          maxWidth: '500px',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Verification Error</h1>
          <p style={{ marginBottom: '2rem', opacity: 0.9 }}>{error}</p>
          <a
            href="/login"
            style={{
              display: 'inline-block',
              padding: '1rem 2rem',
              background: 'white',
              color: '#667eea',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 600,
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid rgba(255, 255, 255, 0.3)',
          borderTop: '4px solid white',
          borderRadius: '50%',
          margin: '0 auto 2rem',
          animation: 'spin 1s linear infinite'
        }} />
        <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Verifying your email...</h1>
        <p style={{ opacity: 0.9 }}>Hang tight while we finish verifying your email.</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
}

